import { http } from "@/configurations/axios";

const BARCODE_LOOKUP_ENDPOINT = "produtos/consulta-cod-barra";
const BARCODE_LOOKUP_TIMEOUT = 10000;
const GTIN_LENGTHS = [8, 12, 13, 14];

export class BarcodeLookupError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "BarcodeLookupError";
    this.code = code;
  }
}

export const normalizeBarcode = (value) =>
  String(value ?? "").replace(/\D/g, "");

export const isValidGtin = (value) => {
  const barcode = normalizeBarcode(value);

  if (!GTIN_LENGTHS.includes(barcode.length)) return false;

  const digits = barcode.split("").map(Number);
  const checkDigit = digits.pop();
  const sum = digits
    .reverse()
    .reduce(
      (total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1),
      0
    );

  return (10 - (sum % 10)) % 10 === checkDigit;
};

export const mapBackendProduct = (payload) => {
  const product = payload?.product || payload?.data || payload;

  if (!product || product.error) return null;

  const name = String(
    product.nome || product.Nome || product.name || product.product_name || ""
  ).trim();
  const brand = String(
    product.marca || product.Marca || product.brand || product.brands || ""
  ).trim();

  if (!name && !brand) return null;

  const mappedProduct = {
    name,
    brand,
    source: "Kodebar",
  };

  if (product.thumbnail) {
    mappedProduct.thumbnail = String(product.thumbnail).trim();
  }

  if (product.thumbnail_data) {
    mappedProduct.thumbnailData = String(product.thumbnail_data);
    mappedProduct.thumbnailFilename = String(
      product.thumbnail_filename || "produto.jpg"
    );
    mappedProduct.thumbnailMimeType = String(
      product.thumbnail_mime_type || "image/jpeg"
    );
  }

  return mappedProduct;
};

export const createProductImageFile = (product) => {
  const dataUrl = product?.thumbnailData;
  const match = /^data:(image\/(?:jpeg|png));base64,([A-Za-z0-9+/=]+)$/.exec(
    dataUrl || ""
  );

  if (!match) return null;

  try {
    const binary = atob(match[2]);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return new File([bytes], product.thumbnailFilename || "produto.jpg", {
      type: product.thumbnailMimeType || match[1],
    });
  } catch (error) {
    return null;
  }
};

export async function lookupProductByBarcode(value, client = http) {
  const barcode = normalizeBarcode(value);

  if (!isValidGtin(barcode)) {
    throw new BarcodeLookupError(
      "INVALID_BARCODE",
      "Informe um código de barras GTIN/EAN válido."
    );
  }

  let response;

  try {
    response = await client.post(
      BARCODE_LOOKUP_ENDPOINT,
      {
        cod_barras: barcode,
      },
      {
        timeout: BARCODE_LOOKUP_TIMEOUT,
      }
    );
  } catch (error) {
    if (error?.response?.status === 404) {
      throw new BarcodeLookupError(
        "PRODUCT_NOT_FOUND",
        "Produto não encontrado para este código de barras."
      );
    }

    if (error?.code === "ECONNABORTED") {
      throw new BarcodeLookupError(
        "REQUEST_TIMEOUT",
        "A consulta demorou mais que o esperado. Tente novamente."
      );
    }

    throw new BarcodeLookupError(
      "SERVICE_UNAVAILABLE",
      "Não foi possível consultar o código de barras agora. Tente novamente."
    );
  }

  const product = mapBackendProduct(response.data);

  if (!product) {
    throw new BarcodeLookupError(
      "PRODUCT_NOT_FOUND",
      "Produto não encontrado para este código de barras."
    );
  }

  return product;
}
